/**
 * territorialEntryCamera — resolveTerritorialEntryCamera
 *
 * Resuelve la cámara inicial de /territorio de forma adaptable al tamaño
 * real del núcleo urbano del proyecto, sin depender de ajustes manuales.
 *
 * Estrategia de decisión (en orden de prioridad):
 *   1. persisted   — vista curada: ENTRY_VIEW_REGISTRY hoy, backend mañana.
 *   2. network-percentile — bbox P10-P90 de red estructurante (primary+secondary).
 *   3. network-bbox       — bbox completo de la red preview.
 *   4. project-bounds     — bounds administrativos del proyecto.
 *   5. fallback           — cámara segura genérica.
 *
 * La función es pura: sin React, sin MapLibre, sin side-effects.
 * Recibe datos geométricos y devuelve parámetros de cámara listos para easeTo.
 */
import type { FeatureCollection } from 'geojson';

// ─── Tipos públicos ───────────────────────────────────────────────────────────

export interface ProjectBounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

/**
 * Vista curada que prevalece sobre el algoritmo automático.
 * Hoy proviene de ENTRY_VIEW_REGISTRY; en el futuro de project.territorialEntryView.
 */
export interface PersistedView {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
}

/** Parámetros de cámara resueltos, listos para map.easeTo(). */
export interface TerritorialCamera {
  center: [number, number];
  zoom: number;
  pitch: number;
  bearing: number;
  /** Estrategia que produjo esta cámara — útil para diagnóstico. */
  source: 'persisted' | 'network-percentile' | 'network-bbox' | 'project-bounds' | 'fallback';
}

export interface ResolveCameraInput {
  featureCollection: FeatureCollection;
  projectBounds: ProjectBounds;
  /**
   * Vista curada: cuando existe, prevalece sin calcular nada.
   * Hoy: ENTRY_VIEW_REGISTRY[projectId].
   * Futuro: project.territorialEntryView del backend.
   */
  persistedView?: PersistedView | null;
  /** Ancho efectivo del viewport en px. Por defecto 1440 (desktop). */
  viewportWidthPx?: number;
  /** Alto efectivo del viewport en px. Por defecto 900 (desktop). */
  viewportHeightPx?: number;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

/** Pitch por defecto: perspectiva territorial sin inclinación extrema. */
const DEFAULT_PITCH = 55;

/** Bearing por defecto: rotación leve que rompe la monotonía norte-sur. */
const DEFAULT_BEARING = -18;

/** Rango de zoom para proyectos urbanos (metropolitano → barrio denso). */
const ZOOM_MIN = 10.0;
const ZOOM_MAX = 14.5;

/** Stops de padding sustraídos al zoom calculado para dar margen visual. */
const ZOOM_PADDING_STOPS = 0.5;

/** Dimensiones de viewport por defecto cuando no se proveen. */
const DEFAULT_VIEWPORT_W = 1440;
const DEFAULT_VIEWPORT_H = 900;

/** Clases que definen el núcleo estructurante (sin residencial). */
const STRUCTURAL_CLASSES = new Set(['primary', 'secondary']);

/** Set extendido si el estructurante tiene pocas features. */
const EXTENDED_CLASSES = new Set(['primary', 'secondary', 'tertiary']);

/** Mínimo de coordenadas para confiar en una computación de percentil. */
const MIN_COORDS_FOR_PERCENTILE = 20;

// ─── Tipos internos ───────────────────────────────────────────────────────────

type Coord = [number, number]; // [lng, lat]

interface BBox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

// ─── Helpers geométricos ──────────────────────────────────────────────────────

function collectCoords(fc: FeatureCollection, classFilter: Set<string>): Coord[] {
  const coords: Coord[] = [];
  for (const feature of fc.features) {
    if (feature.geometry.type !== 'LineString') continue;
    const cls = feature.properties?.roadClass as string | undefined;
    if (!cls || !classFilter.has(cls)) continue;
    for (const c of feature.geometry.coordinates) {
      coords.push([c[0], c[1]]);
    }
  }
  return coords;
}

function allNetworkCoords(fc: FeatureCollection): Coord[] {
  const coords: Coord[] = [];
  for (const feature of fc.features) {
    if (feature.geometry.type !== 'LineString') continue;
    for (const c of feature.geometry.coordinates) {
      coords.push([c[0], c[1]]);
    }
  }
  return coords;
}

/** Valor en el cuantil p (0–1) de un array ya ordenado ascendentemente. */
function quantile(sorted: number[], p: number): number {
  const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor(p * sorted.length)));
  return sorted[idx];
}

function percentileBbox(coords: Coord[], pLow: number, pHigh: number): BBox {
  const lngs = coords.map(c => c[0]).sort((a, b) => a - b);
  const lats = coords.map(c => c[1]).sort((a, b) => a - b);
  return {
    minLng: quantile(lngs, pLow),
    maxLng: quantile(lngs, pHigh),
    minLat: quantile(lats, pLow),
    maxLat: quantile(lats, pHigh),
  };
}

function minMaxBbox(coords: Coord[]): BBox {
  let minLng = Infinity,
    maxLng = -Infinity;
  let minLat = Infinity,
    maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return { minLng, maxLng, minLat, maxLat };
}

function bboxCenter(bbox: BBox): Coord {
  return [(bbox.minLng + bbox.maxLng) / 2, (bbox.minLat + bbox.maxLat) / 2];
}

// ─── Fórmula de zoom ─────────────────────────────────────────────────────────

/**
 * Calcula el zoom para encuadrar un bbox en el viewport.
 *
 * Fórmula Web Mercator estándar para la restricción de ancho:
 *   z = log2(360 × viewportPx / (bboxDeg × 256))
 *
 * Para el alto, los grados de latitud se convierten a grados "equatoriales"
 * dividiendo por cos(lat), ya que Mercator los estira en latitudes altas.
 * Se toma el mínimo de ambas restricciones (la más apretada gana).
 *
 * Calibración: San Juan (-31.5°S), P10-P90 ≈ 0.267° × 0.133° lng/lat
 * → zoom ≈ 12.4 con viewport 1440×900 y 0.5 stops de padding. ✓
 */
function zoomFromBbox(bbox: BBox, viewportW: number, viewportH: number): number {
  const widthDeg = bbox.maxLng - bbox.minLng;
  const heightDeg = bbox.maxLat - bbox.minLat;
  const centerLat = (bbox.minLat + bbox.maxLat) / 2;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);

  if (widthDeg <= 0 || heightDeg <= 0 || cosLat <= 0) {
    return (ZOOM_MIN + ZOOM_MAX) / 2; // bbox degenerado → zoom intermedio seguro
  }

  const zWidth = Math.log2((360 * viewportW) / (widthDeg * 256));
  // Restricción de alto: corregir lat degrees por Mercator (1/cosLat más alto = más pixels)
  const zHeight = Math.log2((360 * viewportH * cosLat) / (heightDeg * 256));

  const z = Math.min(zWidth, zHeight) - ZOOM_PADDING_STOPS;
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z));
}

// ─── Resolver principal ───────────────────────────────────────────────────────

/**
 * Resuelve la cámara inicial territorial adaptada al tamaño real del proyecto.
 *
 * Prioridad:
 *   1. persistedView  — vista curada (ENTRY_VIEW_REGISTRY / backend futuro)
 *   2. network-percentile — P10-P90 de primary+secondary (o +tertiary si escaso)
 *   3. network-bbox   — bbox completo de toda la red preview
 *   4. project-bounds — bounds administrativos del proyecto
 *   5. fallback       — cámara genérica segura
 */
export function resolveTerritorialEntryCamera(input: ResolveCameraInput): TerritorialCamera {
  const {
    featureCollection,
    projectBounds,
    persistedView = null,
    viewportWidthPx = DEFAULT_VIEWPORT_W,
    viewportHeightPx = DEFAULT_VIEWPORT_H,
  } = input;

  // 1 — persisted: vista curada tiene prioridad absoluta
  if (persistedView) {
    return { ...persistedView, source: 'persisted' };
  }

  const vW = viewportWidthPx;
  const vH = viewportHeightPx;

  // 2 — network-percentile: P10-P90 del núcleo estructurante
  let structuralCoords = collectCoords(featureCollection, STRUCTURAL_CLASSES);
  if (structuralCoords.length < MIN_COORDS_FOR_PERCENTILE) {
    structuralCoords = collectCoords(featureCollection, EXTENDED_CLASSES);
  }
  if (structuralCoords.length >= MIN_COORDS_FOR_PERCENTILE) {
    const bbox = percentileBbox(structuralCoords, 0.1, 0.9);
    const center = bboxCenter(bbox);
    const zoom = zoomFromBbox(bbox, vW, vH);
    return {
      center,
      zoom,
      pitch: DEFAULT_PITCH,
      bearing: DEFAULT_BEARING,
      source: 'network-percentile',
    };
  }

  // 3 — network-bbox: toda la red disponible (menos robusto que percentil)
  const allCoords = allNetworkCoords(featureCollection);
  if (allCoords.length > 0) {
    const bbox = minMaxBbox(allCoords);
    const center = bboxCenter(bbox);
    const zoom = zoomFromBbox(bbox, vW, vH);
    return { center, zoom, pitch: DEFAULT_PITCH, bearing: DEFAULT_BEARING, source: 'network-bbox' };
  }

  // 4 — project-bounds: bounds administrativos como último recurso real
  const { minLng, maxLng, minLat, maxLat } = projectBounds;
  if (isFinite(minLng)) {
    const bbox = { minLng, maxLng, minLat, maxLat };
    const center = bboxCenter(bbox);
    const zoom = zoomFromBbox(bbox, vW, vH);
    return {
      center,
      zoom,
      pitch: DEFAULT_PITCH,
      bearing: DEFAULT_BEARING,
      source: 'project-bounds',
    };
  }

  // 5 — fallback genérico
  return {
    center: [0, 0],
    zoom: 12,
    pitch: DEFAULT_PITCH,
    bearing: DEFAULT_BEARING,
    source: 'fallback',
  };
}
